import { ChangeDetectorRef, Component, Input, OnInit, inject } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

import { FridgeService } from '../../services/fridge/fridge.service';
import { Api } from '../../services/api/api.service';
import { UserService } from '../../services/user/user.service';
import { ProductService } from '../../services/product/product.service';
import { NotificationService } from '../../services/notification/notification.service';

import Utils from '../../utils';
import { ConfirmationModalComponent } from '@components/confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'app-baskets-modal',
  templateUrl: './baskets-modal.component.html',
  styleUrls: ['./baskets-modal.component.scss'],
  standalone: false,
})
export class BasketsModalComponent implements OnInit {
  private modalController = inject(ModalController);
  private cdRef = inject(ChangeDetectorRef);
  fridgeService = inject(FridgeService);
  productService = inject(ProductService);
  userService = inject(UserService);
  i18n = inject(TranslateService);
  notificationService = inject(NotificationService);

  fridgeCurrencySymbol: string;

  @Input() sequences: any[];

  constructor() {
    this.fridgeCurrencySymbol = 'f';
  }

  sending = false;
  sendingSingle: Record<string, boolean> = {};

  ngOnInit() {}

  async back() {
    await this.modalController.dismiss({ sequences: this.sequences });
  }

  isDepositReturned(basket: any): boolean {
    if (basket.depositReturned !== undefined) {
      return !!basket.depositReturned;
    }
    return !(basket.rentedItems && basket.rentedItems.length > 0);
  }

  canMarkReturned(basket: any): boolean {
    return (
      !!(basket.rentedItems && basket.rentedItems.length > 0) &&
      !basket.paymentCompletionInitiatedAt
    );
  }

  paymentClass(status: string | undefined) {
    switch (status) {
      case 'Paid':
        return 'payment-ok';
      case 'Pending':
        return 'payment-pending';
      case 'Underpaid':
        return 'payment-underpaid';
      default:
        return '';
    }
  }

  async completeRentalReturnAll(basketToReturn: any) {
    const sequence = this.findSequenceByBasketId(basketToReturn?._id);
    const lastBasket = sequence ? this.getLastBasket(sequence) : basketToReturn;
    if (!lastBasket?._id) {
      return;
    }
    const { success } = await ConfirmationModalComponent.show(
      this.modalController,
      this.i18n.instant('rental.modals.mark_rental_as_returned_title'),
      this.i18n.instant('rental.modals.mark_rental_as_returned_question'),
    );
    if (success) {
      const { basket } = await Api.completeRentalBasketAsReturned(lastBasket._id);
      this.sequences = this.sequences.filter((s) => !s.baskets.some((b) => b._id === basket._id));
      this.cdRef.detectChanges();
    }
  }

  private collectBasketIds(): string[] {
    const ids: string[] = [];
    if (this.sequences?.length) {
      this.sequences.forEach((s) => {
        const last = this.getLastBasket(s);
        if (last?._id) {
          ids.push(last._id);
        }
      });
    }
    return Array.from(new Set(ids));
  }

  async showAllOnTablet() {
    if (this.sending) {
      return;
    }
    const basketIds = this.collectBasketIds();
    if (!basketIds.length || !this.fridgeService.activeFridge?._id) {
      return;
    }
    this.sending = true;
    try {
      await Api.showRentalBasketsOnTablet(this.fridgeService.activeFridge._id, basketIds);
      await this.notificationService.notify(
        this.i18n.instant('notification.success.rental_baskets_sent'),
      );
    } catch (e) {
      // await this.notificationService.notifyError(
      //   this.i18n.instant('notification.error.rental_baskets_send_failed'),
      // );
    } finally {
      this.sending = false;
      this.cdRef.detectChanges();
    }
  }

  async showBasketOnTablet(basketId: string) {
    if (!basketId || !this.fridgeService.activeFridge?._id) {
      return;
    }
    const sequence = this.findSequenceByBasketId(basketId);
    const targetBasketId = sequence ? this.getLastBasketId(sequence) : basketId;
    if (!targetBasketId || this.sendingSingle[targetBasketId]) {
      return;
    }
    this.sendingSingle[targetBasketId] = true;
    try {
      await Api.showRentalBasketsOnTablet(this.fridgeService.activeFridge._id, [targetBasketId]);
      await this.notificationService.notify(
        this.i18n.instant('notification.success.rental_basket_sent'),
      );
    } catch (e) {
      // await this.notificationService.notifyError(
      //   this.i18n.instant('notification.error.rental_baskets_send_failed'),
      // );
    } finally {
      this.sendingSingle[targetBasketId] = false;
      this.cdRef.detectChanges();
    }
  }

  money(amount?: string) {
    return Utils.formatMoneyByCurrency(amount || '0', this.fridgeService.activeFridge?.currency);
  }

  private getLastBasket(sequence: any): any | null {
    if (!sequence?.baskets?.length) {
      return null;
    }
    return sequence.baskets[sequence.baskets.length - 1] || null;
  }

  getLastBasketId(sequence: any): string | null {
    return this.getLastBasket(sequence)?._id || null;
  }

  isLastBasket(basket: any, sequence: any): boolean {
    return this.getLastBasket(sequence)?._id === basket?._id;
  }

  private findSequenceByBasketId(basketId: string): any | null {
    if (!basketId || !this.sequences?.length) {
      return null;
    }
    return this.sequences.find((s) => (s.baskets || []).some((b) => b?._id === basketId)) || null;
  }
}
